import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Loader2, MapPin } from 'lucide-react';
import { callOffAPI } from '../../../lib/api';
import type { DirectDeliveryOrder, WizardDelivery, CallOffDirectDeliverySource } from '../../../lib/api';
import { Dialog, DialogContent, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Checkbox } from '../../ui/checkbox';
import { toast } from 'sonner';
import { DEFAULT_DIRECT_COLUMNS, type LocalColumn } from './DirectDeliveryColumns';
import { useCallOffConfig } from '../../../hooks/useCallOffConfig';
import { useCommodityTypeOptions } from './useCommodityTypeOptions';
import DirectDeliveryToolbar from './DirectDeliveryToolbar';
import OrderRow from './DirectDeliveryOrderRow';
import { matchesPartPrefixFilter, type PartPrefixFilter } from '../shared/partPrefixFilter';

/**
 * Resolve the source code for the supplier this call-off is being done for,
 * by matching the supplier name against configured direct-delivery sources.
 * The call-off is tied to a specific supplier so the direct-delivery list
 * should be scoped to that supplier's source rather than letting the user
 * pick any source.
 */
function supplierToSourceCode(
  supplierName: string | undefined,
  sources: CallOffDirectDeliverySource[],
): string {
  if (!supplierName) return '';
  const upper = supplierName.toUpperCase();
  const match = sources.find((s) => {
    if (!s.is_active) return false;
    const token = (s.supplier_match_token || '').trim().toUpperCase();
    return token && upper.includes(token);
  });
  return match ? match.code : '';
}

interface DirectDeliveryModalProps {
  open: boolean;
  onClose: () => void;
  onAdd: (deliveries: WizardDelivery[]) => void;
  supplierId: number;
  supplierName?: string;
  existingFillPercent: number;
  editDelivery?: WizardDelivery;
}

function useLocalColumns(defaults: LocalColumn[]) {
  const [columns, setColumns] = useState<LocalColumn[]>(defaults);

  const toggle = useCallback((columnId: string) => {
    setColumns(prev => prev.map(c =>
      c.id === columnId ? { ...c, visible: !c.visible } : c
    ));
  }, []);

  const moveUp = useCallback((columnId: string) => {
    setColumns(prev => {
      const sorted = [...prev].sort((a, b) => a.sortOrder - b.sortOrder);
      const idx = sorted.findIndex(c => c.id === columnId);
      if (idx <= 0) return prev;
      const newOrder = sorted.map((col, i) => {
        if (i === idx) return { ...col, sortOrder: i - 1 };
        if (i === idx - 1) return { ...col, sortOrder: i + 1 };
        return { ...col, sortOrder: i };
      });
      return newOrder;
    });
  }, []);

  const visible = useMemo(
    () => [...columns].sort((a, b) => a.sortOrder - b.sortOrder).filter(c => c.visible),
    [columns]
  );

  return { columns, visible, toggle, moveUp };
}

export default function DirectDeliveryModal({
  open, onClose, onAdd, supplierId, supplierName, existingFillPercent, editDelivery,
}: DirectDeliveryModalProps) {
  const [orders, setOrders] = useState<DirectDeliveryOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedOrderIds, setSelectedOrderIds] = useState<Set<string>>(new Set());
  const [expandedOrderIds, setExpandedOrderIds] = useState<Set<string>>(new Set());

  // Per-customer configuration (sources list + routing origin).
  const { config } = useCallOffConfig();

  // Filters — source code is locked to the call-off's supplier so the user
  // can't accidentally pull orders for a different supplier.
  const lockedFactoryCode = useMemo(
    () => supplierToSourceCode(supplierName, config.direct_delivery_sources),
    [supplierName, config.direct_delivery_sources],
  );
  const [searchFilter, setSearchFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  // Client-side row filters (applied at the order level — an order is kept if
  // it has at least one product passing the active filters). "partFilter"
  // mirrors the supplier-side Current Stock filter; "commodityFilter" narrows
  // to a single commodity type ("all" = no filter).
  const [partFilter, setPartFilter] = useState<PartPrefixFilter>('all');
  const [commodityFilter, setCommodityFilter] = useState('all');

  // Column management
  const { columns, visible: visibleCols, toggle: toggleCol, moveUp: moveColUp } = useLocalColumns(DEFAULT_DIRECT_COLUMNS);

  const hasPrefilledRef = useRef(false);

  const fetchOrders = useCallback(async () => {
    // No matching active source for this supplier → show no orders rather
    // than letting the request omit the factory_filter and surface every
    // customer's items. Source matching is the only scoping mechanism for
    // direct-delivery rows, so an unmatched supplier means "not configured".
    if (!lockedFactoryCode) {
      setOrders([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const params: Record<string, string | number | boolean> = {
        supplier_id: supplierId,
        factory_filter: lockedFactoryCode,
      };
      if (dateFrom) params['date_from'] = dateFrom;
      if (dateTo) params['date_to'] = dateTo;
      if (searchFilter) params['search'] = searchFilter;

      const res = await callOffAPI.getDirectDeliveryOrders(params);
      setOrders(res.data || []);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load direct delivery orders';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [supplierId, dateFrom, dateTo, searchFilter, lockedFactoryCode]);

  useEffect(() => {
    if (open) { hasPrefilledRef.current = false; }
  }, [open]);

  useEffect(() => {
    if (open) { fetchOrders(); }
  }, [open, fetchOrders]);

  // Pre-fill selected orders when editing
  useEffect(() => {
    if (!open || hasPrefilledRef.current || orders.length === 0) return;
    if (editDelivery) {
      if (editDelivery.salesOrderId) {
        setSelectedOrderIds(new Set([editDelivery.salesOrderId]));
        setExpandedOrderIds(new Set([editDelivery.salesOrderId]));
      } else {
        const editAddr = (editDelivery.address || '').trim().toLowerCase();
        const editPostcode = (editDelivery.postcode || '').trim().toLowerCase();
        const matched = orders.find(o => {
          const oAddr = (o.address || '').trim().toLowerCase();
          const oPostcode = (o.postcode || '').trim().toLowerCase();
          return editPostcode && oPostcode
            ? oAddr === editAddr && oPostcode === editPostcode
            : oAddr === editAddr;
        });
        if (matched) {
          setSelectedOrderIds(new Set([matched.id]));
          setExpandedOrderIds(new Set([matched.id]));
        }
      }
      hasPrefilledRef.current = true;
    } else {
      setSelectedOrderIds(new Set());
      hasPrefilledRef.current = true;
    }
  }, [open, editDelivery, orders]);

  const toggleOrder = (orderId: string) => {
    setSelectedOrderIds(prev => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  };

  const toggleExpanded = (orderId: string) => {
    setExpandedOrderIds(prev => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const allVisibleSelected = visibleOrders.length > 0
      && visibleOrders.every(o => selectedOrderIds.has(o.id));
    if (allVisibleSelected) setSelectedOrderIds(new Set());
    else setSelectedOrderIds(new Set(visibleOrders.map(o => o.id)));
  };

  // Commodity types carried by the loaded orders. Merged with the configured
  // list in the hook below, so the dropdown offers every commodity type rather
  // than only those that happen to appear on screen.
  const commodityTypesInRows = useMemo(() => {
    const set = new Set<string>();
    for (const o of orders) {
      for (const p of o.products) {
        if (p.commodity_type) set.add(p.commodity_type);
      }
    }
    return Array.from(set);
  }, [orders]);

  const commodityOptions = useCommodityTypeOptions(commodityTypesInRows);

  // Orders after the Parts and commodity filters. An order is kept when at
  // least one of its products passes the active filters.
  const visibleOrders = useMemo(
    () => orders.filter(o => {
      const filterActive = partFilter !== 'all' || commodityFilter !== 'all';
      if (!filterActive) return true;
      return o.products.some(p => {
        if (!matchesPartPrefixFilter(partFilter, p.code, p.desc)) return false;
        if (commodityFilter !== 'all' && (p.commodity_type || '') !== commodityFilter) return false;
        return true;
      });
    }),
    [orders, partFilter, commodityFilter]
  );

  const selectedOrders = useMemo(
    () => orders.filter(o => selectedOrderIds.has(o.id)),
    [orders, selectedOrderIds]
  );

  const modalFillPercent = useMemo(() => {
    let fill = 0;
    for (const order of selectedOrders) {
      for (const product of order.products) {
        if (product.load_qty > 0) fill += product.qty / product.load_qty;
      }
    }
    return Math.round(fill * 100);
  }, [selectedOrders]);

  const totalFillPercent = existingFillPercent + modalFillPercent;
  const totalUnits = selectedOrders.reduce(
    (sum, order) => sum + order.products.reduce((s, p) => s + p.qty, 0), 0
  );

  const handleAdd = () => {
    if (selectedOrders.length === 0) { toast.error('Please select at least one order'); return; }
    const deliveries: WizardDelivery[] = selectedOrders.map(order => ({
      type: 'direct' as const,
      destinationId: null,
      destinationName: order.customer,
      salesOrderId: order.id,
      address: order.address,
      postcode: order.postcode,
      deliveryDate: order.delivery_date || '',
      parts: order.products.map(p => ({
        partId: null,
        partNumber: p.code,
        description: p.desc,
        qty: p.qty,
        palletQty: 0,
        loadQty: p.load_qty,
      })),
    }));
    onAdd(deliveries);
  };

  const handleClose = () => {
    setSelectedOrderIds(new Set());
    setExpandedOrderIds(new Set());
    setSearchFilter('');
    setDateFrom('');
    setDateTo('');
    setPartFilter('all');
    setCommodityFilter('all');
    hasPrefilledRef.current = false;
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="max-w-[95vw] max-h-[95vh] w-full h-full flex flex-col p-0" aria-describedby={undefined}>
        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-4 border-b">
          <MapPin className="h-5 w-5 text-blue-500" />
          <DialogTitle className="text-lg font-semibold">
            {editDelivery ? 'Edit Direct Delivery' : 'Add Direct Deliveries'}
          </DialogTitle>
        </div>

        <DirectDeliveryToolbar
          searchFilter={searchFilter}
          setSearchFilter={setSearchFilter}
          supplierName={supplierName}
          lockedFactoryCode={lockedFactoryCode}
          dateFrom={dateFrom}
          setDateFrom={setDateFrom}
          dateTo={dateTo}
          setDateTo={setDateTo}
          partFilter={partFilter}
          setPartFilter={setPartFilter}
          commodityFilter={commodityFilter}
          setCommodityFilter={setCommodityFilter}
          commodityOptions={commodityOptions}
          columns={columns}
          onToggleColumn={toggleCol}
          onMoveColumnUp={moveColUp}
          routingOrigin={config.routing_origin}
          selectedPostcodes={selectedOrders.map(o => o.postcode).filter(Boolean)}
          totalFillPercent={totalFillPercent}
          existingFillPercent={existingFillPercent}
          modalFillPercent={modalFillPercent}
          isEdit={Boolean(editDelivery)}
        />

        {/* Orders table */}
        <div className="flex-1 overflow-auto px-6 py-2">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-20 text-muted-foreground">
              {lockedFactoryCode
                ? 'No direct delivery orders found for the selected criteria.'
                : 'No direct-delivery source is configured for this supplier. Ask an admin to add a matching source under Admin → Call-Off → Direct Delivery Sources.'}
            </div>
          ) : visibleOrders.length === 0 ? (
            <div className="text-center py-20 text-muted-foreground text-sm">
              All {orders.length} orders were hidden by the active filters.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-20 bg-background border-b">
                <tr className="text-left text-xs text-muted-foreground">
                  <th className="py-2 px-2 w-8" />
                  <th className="py-2 px-2 w-8">
                    <Checkbox
                      checked={visibleOrders.length > 0 && visibleOrders.every(o => selectedOrderIds.has(o.id))}
                      onCheckedChange={toggleSelectAll}
                    />
                  </th>
                  {visibleCols.map(col => (
                    <th key={col.id} className={`py-2 px-2 font-medium whitespace-nowrap ${col.id === 'fill_percent' ? 'text-right' : ''}`}>
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y">
                {visibleOrders.map(order => (
                  <OrderRow
                    key={order.id}
                    order={order}
                    visibleCols={visibleCols}
                    isSelected={selectedOrderIds.has(order.id)}
                    isExpanded={expandedOrderIds.has(order.id)}
                    onToggleSelect={() => toggleOrder(order.id)}
                    onToggleExpand={() => toggleExpanded(order.id)}
                    routingOrigin={config.routing_origin}
                  />
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-3 border-t bg-muted/30">
          <span className="text-sm text-muted-foreground">
            {selectedOrders.length} {selectedOrders.length === 1 ? 'order' : 'orders'} selected &middot;{' '}
            {totalUnits} units
          </span>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleClose}>Cancel</Button>
            <Button onClick={handleAdd} disabled={selectedOrders.length === 0}>
              {editDelivery ? 'Update Deliveries' : 'Add Deliveries to Call-Off'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
